import api from 'api/config';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
} from 'store/slices/authSlice';

/**
 * Authentication Service for user authentication and session management
 * Provides methods for login, logout, signup, and authentication state management
 *
 * Backend endpoints:
 * - GET /check-auth - Verify authentication status
 * - POST /login - User login
 * - POST /logout - User logout
 * - POST /signup - User registration
 * - POST /update-password - Update user password
 * - GET /user - Get user details
 *
 * Token Management:
 * - Uses sessionStorage for auth tokens (cleared on tab close)
 * - Supports X-Auth-Token header for incognito mode fallback
 * - Automatic token cleanup on logout and auth failures
 */
export const authService = {
  /**
   * Check authentication status and restore session
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<boolean>} True if authenticated, false otherwise
   */
  checkAuth: async (dispatch) => {
    try {
      dispatch(loginStart());
      const response = await api.get('/check-auth');
      if (response.data?.authenticated && response.data?.user) {
        dispatch(loginSuccess(response.data.user));
        return true;
      }
      // Clear stored tokens if auth check fails
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
      dispatch(logout());
      return false;
    } catch (error) {
      // Clear stored tokens on auth error
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
      dispatch(logout());
      return false;
    }
  },

  /**
   * User login with credentials
   * @param {string} username - Username
   * @param {string} password - Password
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<Object>} Login response with user data
   * @returns {Promise<Object>} response.user - Authenticated user details
   */
  login: async (username, password, dispatch) => {
    try {
      dispatch(loginStart());
      const response = await api.post('/login', { username, password });

      // Store token from X-Auth-Token header for incognito mode fallback
      const headerToken = response.headers['x-auth-token'];
      if (headerToken) {
        // Use sessionStorage for better security (cleared on tab close)
        sessionStorage.setItem('auth_token', headerToken);
        // Remove from localStorage if it exists (migration)
        localStorage.removeItem('auth_token');
      }

      dispatch(loginSuccess(response.data.user));
      return response.data;
    } catch (error) {
      dispatch(loginFailure(error.response?.data?.error || error.message));
      throw error;
    }
  },

  /**
   * User logout and session cleanup
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<Object>} Logout confirmation
   */
  logout: async (dispatch) => {
    try {
      const response = await api.post('/logout');
      // Clear stored tokens on successful logout
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
      dispatch(logout());
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear tokens and dispatch logout even if the API call fails
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token');
      dispatch(logout());
      throw error;
    }
  },

  /**
   * User registration
   * @param {string} username - Desired username
   * @param {string} password - Password
   * @param {string} email - Email address
   * @param {string} phoneNumber - Optional phone number
   * @param {string} inviteHash - Optional invite hash for beta access
   * @returns {Promise<Object>} Registration response
   */
  signup: async (
    username,
    password,
    email,
    phoneNumber = '',
    inviteHash = ''
  ) => {
    try {
      const response = await api.post('/signup', {
        username,
        password,
        email,
        phone_number: phoneNumber,
        invite_hash: inviteHash,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  /**
   * Update user password
   * @param {number|string} userId - User ID
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password update confirmation
   */
  updatePassword: async (userId, oldPassword, newPassword) => {
    try {
      const response = await api.post('/update-password', {
        user_id: userId,
        old_password: oldPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  /**
   * Get user details by ID
   * @param {number|string} userId - User ID to fetch
   * @returns {Promise<Object>} User details
   */
  getUser: async (userId) => {
    try {
      const response = await api.get('/user', { params: { user_id: userId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  /**
   * Helper method to check if user has a stored token
   * Useful for incognito mode detection
   * @returns {boolean} True if token exists in storage
   */
  hasStoredToken: () => {
    return !!(
      sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token')
    );
  },

  /**
   * Helper method to clear stored tokens
   * Removes tokens from both sessionStorage and localStorage
   */
  clearStoredToken: () => {
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('auth_token');
  },

  /**
   * Helper method to get stored token
   * Checks sessionStorage first, then localStorage
   * @returns {string|null} Auth token if found, null otherwise
   */
  getStoredToken: () => {
    return (
      sessionStorage.getItem('auth_token') || localStorage.getItem('auth_token')
    );
  },
};
